import { useEffect, useRef } from "react"

/**
 * A canvas overlay that puffs animated smoke up from each smoke-test item's
 * title — black while a check is pending, green once it's done. Bursts fire when
 * a train's checklist first loads (tab open / train switch) and again for a
 * single item when it's checked or unchecked. A burst emits for ~27s and its
 * thick particles rise buoyantly from the title, leaning ~30° off vertical to
 * the right (up and gently right), climbing to the top of the list and billowing
 * wider as they go; the whole thing is fully spent by ~30s and the loop stops
 * itself once nothing is left to draw.
 * Honours prefers-reduced-motion and the viewer's own on/off toggle (`enabled`)
 * — renders nothing and never animates when off.
 *
 * Positions are read from the DOM at burst time via `[data-smoke-id]` on each
 * title, measured relative to `containerRef`, so the plumes track the current
 * layout. Purely decorative — the canvas is pointer-events-none, so clicks and
 * checkboxes underneath keep working.
 */

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  age: number
  life: number
  size: number
  wob: number
  green: boolean
}

interface Emitter {
  x: number
  y: number
  green: boolean
  startAt: number // performance.now() timestamp to begin emitting (allows a stagger)
  endAt: number // …and to stop
  acc: number // fractional-particle accumulator
}

// Long-lived plumes: emit for ~27s (particles rise out the top of the list in a
// couple seconds, so the emit window is what sets the ~30s visible duration).
const EMIT_MS = 27000 // how long one burst keeps emitting
const RATE = 13 // particles/sec per emitter — dense, thick smoke
const MAX_PARTICLES = 1400 // safety cap across all emitters
const BUOY = 16 // upward acceleration (px/s²) — smoke speeds up as it rises
const DRIFT_ACC = 3 // horizontal acceleration (px/s²), rightward — sustains the up-right lean
const VY_MIN = -190 // clamp terminal upward speed (px/s)
// Launch direction: ~60° (math convention) = ~30° off vertical toward the RIGHT
// — mostly up, gently right — with a little spread; buoyancy lifts it further.
const ANG_MIN = 50
const ANG_MAX = 70

const rand = (a: number, b: number) => a + Math.random() * (b - a)

function reducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
  } catch {
    return false
  }
}

export function SmokeLayer({
  containerRef,
  items,
  burstKey,
  enabled = true,
}: {
  containerRef: React.RefObject<HTMLElement>
  items: { id: string; done: boolean }[]
  burstKey: number | string | null
  enabled?: boolean
}) {
  // Off when the viewer disabled it or the OS asks for reduced motion.
  const on = enabled && !reducedMotion()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const emitters = useRef<Emitter[]>([])
  const particles = useRef<Particle[]>([])
  const raf = useRef<number | null>(null)
  const last = useRef<number>(0)
  // Remembered so we can tell a first load / train switch (burst all) from a
  // single item's done-state flip (burst just that item).
  const prevKey = useRef<number | string | null>(null)
  const prevDone = useRef<Map<string, boolean>>(new Map())
  const prevOn = useRef(false) // to detect an off→on toggle (fire a fresh burst as feedback)

  // Add an emitter anchored to an item's title, measured relative to the layer.
  // `delay` staggers a group of bursts so they don't all start on one frame.
  const addEmitter = (id: string, green: boolean, delay = 0) => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return
    const el = container.querySelector<HTMLElement>(`[data-smoke-id=${JSON.stringify(id)}]`)
    if (!el) return
    const cRect = container.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    const startAt = performance.now() + delay
    emitters.current.push({
      x: r.left - cRect.left + 6, // start at the title's left edge, drifting right into open space
      y: r.top - cRect.top + r.height / 2,
      green,
      startAt,
      endAt: startAt + EMIT_MS,
      acc: 0,
    })
  }

  const ensureRunning = () => {
    if (raf.current != null) return
    last.current = performance.now()
    raf.current = requestAnimationFrame(tick)
  }

  const tick = (now: number) => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) {
      raf.current = null
      return
    }
    const dpr = window.devicePixelRatio || 1
    const dt = Math.min(0.05, (now - last.current) / 1000) // clamp big gaps (tab was hidden)
    last.current = now

    // Spawn from active emitters (drop only those fully past their window; keep
    // not-yet-started ones so the loop stays alive through a stagger).
    emitters.current = emitters.current.filter((e) => now < e.endAt)
    for (const e of emitters.current) {
      if (now < e.startAt) continue
      e.acc += RATE * dt
      while (e.acc >= 1 && particles.current.length < MAX_PARTICLES) {
        e.acc -= 1
        const ang = (rand(ANG_MIN, ANG_MAX) * Math.PI) / 180
        const spd = rand(58, 92)
        particles.current.push({
          x: e.x + rand(-6, 6),
          y: e.y + rand(-3, 3),
          vx: Math.cos(ang) * spd, // ~cos(60°) > 0 → rightward
          vy: -Math.sin(ang) * spd, // screen-up (negative); buoyancy adds more
          age: 0,
          life: rand(6, 8),
          size: rand(10, 16), // thick puffs
          wob: rand(0, Math.PI * 2),
          green: e.green,
        })
      }
    }

    // Update + draw.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr)
    ctx.globalCompositeOperation = "source-over"
    const next: Particle[] = []
    for (const p of particles.current) {
      p.age += dt
      const t = p.age / p.life
      if (t >= 1) continue
      // Buoyant rise: accelerate upward (clamped) with a sustained leftward drift,
      // so the plume leans up-and-to-the-left and climbs to the top.
      p.vy = Math.max(VY_MIN, p.vy - BUOY * dt)
      p.vx += DRIFT_ACC * dt
      // Natural curl: horizontal wobble on top of the steady drift.
      const wobble = Math.sin(p.wob + p.age * 2) * 12
      p.x += (p.vx + wobble) * dt
      p.y += p.vy * dt
      const radius = p.size * (1 + t * 2.8) // keep expanding as it rises
      const alpha = 0.55 * (1 - t) * Math.min(1, t * 5) // thick, fades in fast / out slow
      const rgb = p.green ? "16,185,129" : "40,40,48"
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius)
      g.addColorStop(0, `rgba(${rgb},${alpha})`)
      g.addColorStop(1, `rgba(${rgb},0)`)
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2)
      ctx.fill()
      next.push(p)
    }
    particles.current = next

    if (emitters.current.length || particles.current.length) {
      raf.current = requestAnimationFrame(tick)
    } else {
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr)
      raf.current = null
    }
  }

  // Keep the canvas backing store sized to the container.
  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return
    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.max(1, Math.floor(container.clientWidth * dpr))
      canvas.height = Math.max(1, Math.floor(container.clientHeight * dpr))
      canvas.style.width = `${container.clientWidth}px`
      canvas.style.height = `${container.clientHeight}px`
    }
    resize()
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null
    ro?.observe(container)
    return () => ro?.disconnect()
  }, [containerRef])

  // Fire bursts: everything on first load / train switch or a fresh turn-on, one
  // item on a done flip.
  useEffect(() => {
    const turnedOn = on && !prevOn.current
    prevOn.current = on
    if (!on || burstKey == null || !items.length) {
      prevKey.current = burstKey
      prevDone.current = new Map(items.map((i) => [i.id, i.done]))
      return
    }
    if (turnedOn) prevKey.current = null // re-enabling reads as a fresh checklist → burst all
    if (burstKey !== prevKey.current) {
      // New checklist in view — a puff from every item, staggered a touch (via a
      // per-emitter start delay, not timers) so they don't all fire on one frame.
      items.forEach((i, idx) => addEmitter(i.id, i.done, Math.min(idx * 70, 700)))
    } else {
      for (const i of items) {
        if (prevDone.current.get(i.id) !== i.done) addEmitter(i.id, i.done)
      }
    }
    prevKey.current = burstKey
    prevDone.current = new Map(items.map((i) => [i.id, i.done]))
    // Self-healing: start the loop if there's anything queued. Safe to call when
    // already running, and revives it after a StrictMode remount cancels the RAF.
    if (emitters.current.length || particles.current.length) ensureRunning()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs drive the rest
  }, [items, burstKey, on])

  // Stop the loop on unmount.
  useEffect(() => {
    return () => {
      if (raf.current != null) cancelAnimationFrame(raf.current)
      raf.current = null
    }
  }, [])

  if (!on) return null
  return <canvas ref={canvasRef} aria-hidden className="pointer-events-none absolute inset-0 z-10" />
}
